import {useEffect, useRef, useState} from "react";
import type {PickerItem} from "../pickerItem";

const CLOSE_INFO_DELAY_MS = 120;

export interface InfoCardState<I extends PickerItem = PickerItem> {
    anchor: HTMLElement;
    item: I;
    pinned: boolean;
}

/**
 * The info card's life: hover opens it, a short grace bridges the gap to the card, a click pins
 * it, a new query or a moved popup closes it.
 */
export function useInfoCard<I extends PickerItem>(query: string) {
    const [root, setRoot] = useState<HTMLDivElement | null>(null);
    const [info, setInfo] = useState<InfoCardState<I> | null>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [seen, setSeen] = useState(query);
    if (seen !== query) {
        setSeen(query);
        setInfo(null);
    }

    const cancelClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = null;
    };

    const openInfo = (anchor: HTMLElement, item: I, pinned: boolean) => {
        cancelClose();
        setInfo(current => (
            pinned && current?.item.key === item.key && current.pinned
                ? null
                : {anchor, item, pinned: pinned || (current?.pinned ?? false)}
        ));
    };

    const closeInfoSoon = () => {
        cancelClose();
        closeTimer.current = setTimeout(
            () => setInfo(current => (current?.pinned ? current : null)),
            CLOSE_INFO_DELAY_MS,
        );
    };

    const closeInfo = () => setInfo(null);

    useEffect(() => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
    }, []);

    /*
        The popup is positioned by Floating UI, which writes the mount element's inline style
        whenever the anchor moves or the popup flips above the caret. The card is positioned by
        popper.js against a row inside that element, in a pass that never re-runs, so it would
        be left pointing at where the row used to be.
     */
    const mount = root?.parentElement;

    useEffect(() => {
        if (!info || !mount) return;

        let placement = mount.getAttribute("style");
        const observer = new MutationObserver(() => {
            const next = mount.getAttribute("style");
            if (next === placement) return;

            placement = next;
            setInfo(null);
        });

        observer.observe(mount, {attributeFilter: ["style"]});

        return () => observer.disconnect();
    }, [info, mount]);

    return {root, setRoot, info, openInfo, closeInfoSoon, cancelClose, closeInfo};
}

export default useInfoCard;
